import React, { useEffect, useState, useRef, ReactNode } from 'react';
import { Input } from 'antd';
import classNames from 'classnames';
import QuestionActions from './Actions';
import { QuestionType, IQuestionListItem, ISubmitClozeAnswer } from '@/types';
import Audio from '@/components/audio';
import './index.less';
import { uuid, getQuestionType, popUpComprehensiveQuestionTitle } from '@/utils';
import DOMPurify from 'dompurify';
import { DoCloze } from './cloze-test';
import { DoShortAnswer } from './short-answer-view';
import { useRequest } from 'ahooks';
import { useAppStore } from '@/store';

interface IProps {
    onChangeValue?: (
        questionItem: IQuestionListItem,
        userAnswer: {
            userAnswer: string | number;
            userAnswerImagesList?: string[];
            excelAnswer?: string;
            userClozeAnswers?: ISubmitClozeAnswer[];
        },
        options?: { isFetch?: boolean }
    ) => void; // 选择题目
    parentId?: number;
    questionItem: IQuestionListItem;
    actionNode?: ReactNode;
}

const Question: React.FC<IProps> = props => {
    const { questionItem, actionNode, parentId } = props;
    const { selectList, title, sortNum } = questionItem;
    const [checkList, setCheckList] = useState<string[]>([]); // 多选
    const [checkSingle, setCheckSingle] = useState<string>(); // 单选
    const [checkJudge, setCheckJudge] = useState<number>(); // 判断
    const [inputValue, setInputValue] = useState<string>(''); // 填空
    const [excelAnswer, setExcelAnswer] = useState<string>(''); // excelAnswer
    const inputRef = useRef<any>();

    const { commonStore } = useAppStore();
    const { isAllowCopy } = commonStore.common;

    const initValue = () => {
        const value = questionItem.userAnswer?.userAnswer || '';
        // xss 过滤
        const content = DOMPurify.sanitize(String(value));
        switch (questionItem.questionType) {
            case QuestionType.single:
                setCheckSingle(value ? String(value) : undefined);
                break;
            case QuestionType.multiply:
            case QuestionType.noDirection:
                setCheckList(value ? String(value).split(',') : []);
                break;
            case QuestionType.judge:
                setCheckJudge(value ? Number(value) : undefined);
                break;
            case QuestionType.gapFill:
            case QuestionType.shortAnswer:
                setInputValue(content);
                inputRef.current = content;
                setExcelAnswer(questionItem.userAnswer?.excelAnswer || '');
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        dealyRunCancel();
        initValue();
    }, [questionItem.questionId]);

    const handleCheckSingle = (option: string) => {
        const currentValue = checkSingle === option ? undefined : option;
        setCheckSingle(currentValue);
        props.onChangeValue?.(questionItem, { userAnswer: currentValue || '' });
    };

    const handleCheckMultiple = (option: string) => {
        const currentValue = checkList.includes(option) ? checkList.filter(item => item !== option) : [...checkList, option];
        setCheckList(currentValue.sort());
        props.onChangeValue?.(questionItem, { userAnswer: currentValue.sort().join(',') });
    };

    const handleCheckJudge = (option: number) => {
        const currentValue = checkJudge === option ? undefined : option;
        setCheckJudge(currentValue);
        props.onChangeValue?.(questionItem, { userAnswer: currentValue === undefined ? '' : currentValue });
    };

    const handleInputChange = (value: string) => {
        const content = DOMPurify.sanitize(value);
        setInputValue(content);
        inputRef.current = content;
        props.onChangeValue?.(
            questionItem,
            {
                userAnswer: content,
                excelAnswer: excelAnswer,
            },
            { isFetch: false }
        );
        dealyRunChange(content);
    };

    const { run: dealyRunChange, cancel: dealyRunCancel } = useRequest(
        (content: string) => {
            return new Promise(resolve => {
                resolve(
                    props.onChangeValue?.(questionItem, {
                        userAnswer: content,
                        excelAnswer: excelAnswer,
                    })
                );
            });
        },
        {
            debounceWait: 3000,
            manual: true,
        }
    );

    const getSingleOldOption = () => {
        const list: JSX.Element[] = [];
        for (let i = 0; i < questionItem.optionNum; i++) {
            const option = String.fromCharCode(65 + i);
            list.push(
                <div
                    className={classNames('sub-tiku__question-select-item', {
                        'sub-tiku__question-active': checkSingle === option,
                    })}
                    key={uuid()}
                    onClick={() => handleCheckSingle(option)}
                >
                    <span className="sub-tiku__question-option">{option}</span>
                    <span className="sub-tiku__question-option-content">{option}</span>
                </div>
            );
        }
        return list;
    };

    const getMultipleOldOption = () => {
        const list: JSX.Element[] = [];
        for (let i = 0; i < questionItem.optionNum; i++) {
            const option = String.fromCharCode(65 + i);
            list.push(
                <div
                    className={classNames('sub-tiku__question-select-item', 'sub-tiku__multiply-select-item', {
                        'sub-tiku__question-active': checkList.includes(option),
                    })}
                    key={uuid()}
                    onClick={() => handleCheckMultiple(option)}
                >
                    <span className="sub-tiku__question-option">{option}</span>
                    <span className="sub-tiku__question-option-content">{option}</span>
                </div>
            );
        }
        return list;
    };

    const getDiffQuestionItem = () => {
        switch (questionItem.questionType) {
            case QuestionType.single:
                return selectList?.length ? (
                    selectList.map(item => (
                        <div
                            className={classNames('sub-tiku__question-select-item', {
                                'sub-tiku__question-active': checkSingle === item.option,
                            })}
                            key={item.id || uuid()}
                            onClick={() => handleCheckSingle(item.option)}
                        >
                            <span className="sub-tiku__question-option">{item.option}</span>
                            <span
                                className="sub-tiku__question-option-content"
                                dangerouslySetInnerHTML={{
                                    __html: item.content,
                                }}
                            />
                        </div>
                    ))
                ) : (
                    <>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: questionItem.option,
                            }}
                            style={{ marginBottom: 20 }}
                        />
                        {getSingleOldOption()}
                    </>
                );
            case QuestionType.multiply:
            case QuestionType.noDirection:
                return selectList?.length ? (
                    selectList.map(item => (
                        <div
                            className={classNames('sub-tiku__question-select-item', 'sub-tiku__multiply-select-item', {
                                'sub-tiku__question-active': checkList.includes(item.option),
                            })}
                            key={item.id || uuid()}
                            onClick={() => handleCheckMultiple(item.option)}
                        >
                            <span className="sub-tiku__question-option">{item.option}</span>
                            <span
                                className="sub-tiku__question-option-content"
                                dangerouslySetInnerHTML={{
                                    __html: item.content,
                                }}
                            />
                        </div>
                    ))
                ) : (
                    <>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: questionItem.option,
                            }}
                            style={{ marginBottom: 20 }}
                        />
                        {getMultipleOldOption()}
                    </>
                );
            case QuestionType.judge:
                return (
                    <>
                        <div
                            className={classNames('sub-tiku__question-select-item', {
                                'sub-tiku__question-active': checkJudge === 1,
                            })}
                            onClick={() => handleCheckJudge(1)}
                        >
                            <span className="sub-tiku__question-option">A</span>
                            <span className="sub-tiku__question-option-content">正确</span>
                        </div>
                        <div
                            className={classNames('sub-tiku__question-select-item', {
                                'sub-tiku__question-active': checkJudge === 0,
                            })}
                            onClick={() => handleCheckJudge(0)}
                        >
                            <span className="sub-tiku__question-option">B</span>
                            <span className="sub-tiku__question-option-content">错误</span>
                        </div>
                    </>
                );
            case QuestionType.gapFill:
                return (
                    <div className="sub-tiku__question-short-answer">
                        <div className="sub-tiku__short-answer-title">请做回答：</div>
                        <Input.TextArea
                            autoSize={{ minRows: 4 }}
                            maxLength={5000}
                            onChange={e => handleInputChange(e.target.value)}
                            placeholder="请回答"
                            showCount
                            value={inputValue}
                        />
                    </div>
                );
            case QuestionType.shortAnswer:
                return (
                    <div className="sub-tiku__question-short-answer">
                        <div className="sub-tiku__short-answer-title">请做回答：</div>
                        <DoShortAnswer key={questionItem.questionId} questionItem={questionItem} onChangeValue={props.onChangeValue} />
                    </div>
                );
            case QuestionType.cloze:
                return (
                    <DoCloze
                        questionItem={questionItem}
                        onChangeQuestion={userClozeItem =>
                            props.onChangeValue?.(questionItem, {
                                userAnswer: '',
                                userClozeAnswers: userClozeItem,
                            })
                        }
                    />
                );
            default:
                return '';
        }
    };

    if (questionItem.questionType === QuestionType.comprehensive) {
        return (
            <div className="sub-tiku__question-container sub-tiku__question-split-line">
                <div className="sub-tiku__question-header">
                    <div className="sub-tiku__question-prefix">
                        {!!sortNum && <span className="sub-tiku__question-sort">{sortNum}.</span>}
                        <div className="sub-tiku__question-comprehensive-type">材料</div>
                    </div>
                </div>
                <div className="sub-tiku__question-title-content">
                    {questionItem.titleAudio && <Audio src={questionItem.titleAudio} />}
                    <span dangerouslySetInnerHTML={{ __html: title }} />
                </div>

                <div style={{ textAlign: 'right' }}>
                    <div
                        className="sub-tiku__question-use-btn"
                        onClick={() => {
                            popUpComprehensiveQuestionTitle(title, Boolean(isAllowCopy));
                        }}
                    >
                        弹出
                        <img
                            src="http://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/22/6ef84_20240722112527.png"
                            style={{ marginLeft: 4 }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="sub-tiku__question-container">
            <div className="sub-tiku__question-header">
                <div className="sub-tiku__question-prefix">
                    {sortNum && <span className="sub-tiku__question-sort">{sortNum}.</span>}
                    <div className="sub-tiku__question-type">{getQuestionType(questionItem.questionType)}</div>
                </div>
                <div className="sub-tiku__question-tools-box">
                    {actionNode || <QuestionActions questionItem={questionItem} parentId={parentId} />}
                </div>
            </div>
            <div className="sub-tiku__question-title-content">
                {questionItem.titleAudio && <Audio src={questionItem.titleAudio} />}
                {questionItem.questionType !== QuestionType.cloze && <span dangerouslySetInnerHTML={{ __html: title }} />}
            </div>
            <div style={{ marginTop: 24 }}>{getDiffQuestionItem()}</div>
        </div>
    );
};

export default Question;
